/**
 * usePermissions Hook - Granular Permission System
 * 
 * Provides granular permission checking functionality based on modules and actions
 */

import { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { COLLECTIONS } from '../utils/constants';
import { createPermissionCalculator, getDefaultPermissionsForRole } from '../utils/permissionCalculator';
import { MODULES, ACTIONS } from '../utils/permissions';

export function usePermissions() {
  const { user, role, companyId, isSuperAdmin, isCompanyAdmin, isCompanyUser } = useAuth();
  const [userPermissions, setUserPermissions] = useState(null);
  const [companyLimits, setCompanyLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user permissions from Firestore
  useEffect(() => {
    if (!user?.uid) {
      setUserPermissions(null);
      setLoading(false);
      return;
    }

    console.log('🔍 [usePermissions] Loading permissions for user:', user.uid);

    const unsubscribe = onSnapshot(
      doc(db, COLLECTIONS.USER_PERMISSIONS, user.uid),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          console.log('🔍 [usePermissions] User permissions loaded:', data);
          setUserPermissions(data);
        } else {
          // No custom permissions found, use defaults based on role
          console.log('🔍 [usePermissions] No custom permissions, using defaults for role:', role);
          const defaultPermissions = getDefaultPermissionsForRole(role);
          setUserPermissions(defaultPermissions);
        }
        setLoading(false);
      },
      (error) => {
        console.error('❌ [usePermissions] Error loading user permissions:', error);
        // Fallback to role-based defaults
        const defaultPermissions = getDefaultPermissionsForRole(role);
        setUserPermissions(defaultPermissions);
        setError(error.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [user?.uid, role]);

  // Load company limits if user belongs to a company
  useEffect(() => {
    if (!companyId || isSuperAdmin()) {
      setCompanyLimits(null);
      return;
    }

    console.log('🔍 [usePermissions] Loading company limits for:', companyId);

    const unsubscribe = onSnapshot(
      doc(db, COLLECTIONS.COMPANY_PERMISSIONS, companyId),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          console.log('🔍 [usePermissions] Company limits loaded:', data);
          setCompanyLimits(data.maxPermissions || null);
        } else {
          console.log('🔍 [usePermissions] No company limits found');
          setCompanyLimits(null);
        }
      },
      (error) => {
        console.error('❌ [usePermissions] Error loading company limits:', error);
        setCompanyLimits(null);
      }
    );

    return unsubscribe;
  }, [companyId, isSuperAdmin]);

  // Create permission calculator
  const permissionCalculator = useMemo(() => {
    if (!userPermissions) return null;
    
    return createPermissionCalculator(userPermissions, companyLimits);
  }, [userPermissions, companyLimits]);

  // Permission checking functions
  const hasPermission = (module, action) => {
    if (!permissionCalculator) return false;
    return permissionCalculator.hasPermission(module, action);
  };

  const hasModuleAccess = (module) => {
    if (!permissionCalculator) return false;
    return permissionCalculator.hasModuleAccess(module);
  };

  const canAccessRoute = (route) => {
    if (!permissionCalculator) return false;
    return permissionCalculator.canAccessRoute(route);
  };

  const hasAnyPermission = (permissionChecks) => {
    if (!permissionCalculator) return false;
    return permissionCalculator.hasAnyPermission(permissionChecks);
  };

  const hasAllPermissions = (permissionChecks) => {
    if (!permissionCalculator) return false;
    return permissionCalculator.hasAllPermissions(permissionChecks);
  };

  // Document type specific permissions
  const canAccessDocumentType = (documentType) => {
    if (!permissionCalculator) return false;
    return permissionCalculator.canAccessDocumentType(documentType);
  };

  const canCreateDocumentType = (documentType) => {
    if (!permissionCalculator) return false;
    return permissionCalculator.canCreateDocumentType(documentType);
  };

  const getAllowedDocumentTypes = () => {
    if (!permissionCalculator) return [];
    return permissionCalculator.getAllowedDocumentTypes();
  };

  // Navigation permissions
  const shouldShowNavItem = (navItem) => {
    if (!permissionCalculator) return false;
    return permissionCalculator.shouldShowNavItem(navItem);
  };

  // Utility functions
  const getAccessibleModules = () => {
    if (!permissionCalculator) return [];
    return permissionCalculator.getAccessibleModules();
  };

  const getModuleActions = (module) => {
    if (!permissionCalculator) return [];
    return permissionCalculator.getModuleActions(module);
  };

  const getPermissionSummary = () => {
    if (!permissionCalculator) return null;
    return permissionCalculator.getPermissionSummary();
  };

  // Legacy compatibility functions (for existing code)
  const can = (permission) => {
    // Map old permission strings to new module/action format
    const legacyPermissionMap = {
      'create_company': { module: MODULES.EMPRESAS, action: ACTIONS.CREATE },
      'edit_company': { module: MODULES.EMPRESAS, action: ACTIONS.UPDATE },
      'delete_company': { module: MODULES.EMPRESAS, action: ACTIONS.DELETE },
      'view_all_companies': { module: MODULES.EMPRESAS, action: ACTIONS.READ },
      'create_user': { module: MODULES.USUARIOS, action: ACTIONS.CREATE },
      'edit_user': { module: MODULES.USUARIOS, action: ACTIONS.UPDATE },
      'delete_user': { module: MODULES.USUARIOS, action: ACTIONS.DELETE },
      'view_all_users': { module: MODULES.USUARIOS, action: ACTIONS.READ },
      'view_company_users': { module: MODULES.USUARIOS, action: ACTIONS.READ },
      'upload_document': { module: MODULES.ALBARANES, action: ACTIONS.CREATE },
      'view_company_documents': { module: MODULES.DOCUMENTOS, action: ACTIONS.READ },
      'manage_contacts': { module: MODULES.CONTACTOS, action: ACTIONS.MANAGE },
      'view_contacts': { module: MODULES.CONTACTOS, action: ACTIONS.READ },
    };

    const mapped = legacyPermissionMap[permission];
    if (mapped) {
      return hasPermission(mapped.module, mapped.action);
    }

    // Fallback to role-based check for unmapped permissions
    return false;
  };

  const canAny = (permissionList) => {
    return permissionList.some(permission => can(permission));
  };

  const canAll = (permissionList) => {
    return permissionList.every(permission => can(permission));
  };

  // Specific legacy functions
  const canManageUsers = () => {
    return hasPermission(MODULES.USUARIOS, ACTIONS.CREATE) || 
           hasPermission(MODULES.USUARIOS, ACTIONS.UPDATE);
  };

  const canManageCompany = () => {
    return hasPermission(MODULES.EMPRESAS, ACTIONS.UPDATE) ||
           hasPermission(MODULES.CONFIGURACION, ACTIONS.UPDATE);
  };

  const canViewAllCompanies = () => {
    return hasPermission(MODULES.EMPRESAS, ACTIONS.READ);
  };

  const canUploadDocuments = () => {
    return hasPermission(MODULES.ALBARANES, ACTIONS.CREATE) ||
           hasPermission(MODULES.PARTES_TRABAJO, ACTIONS.CREATE);
  };

  const canViewStats = () => {
    return hasPermission(MODULES.REPORTES, ACTIONS.READ);
  };

  const canManageContacts = () => {
    return hasPermission(MODULES.CONTACTOS, ACTIONS.MANAGE) ||
           hasPermission(MODULES.CONTACTOS, ACTIONS.CREATE);
  };

  const canDeleteDocuments = () => {
    return hasPermission(MODULES.ALBARANES, ACTIONS.DELETE) ||
           hasPermission(MODULES.PARTES_TRABAJO, ACTIONS.DELETE);
  };

  const canEditCompanySettings = () => {
    return hasPermission(MODULES.CONFIGURACION, ACTIONS.UPDATE);
  };

  const getAllPermissions = () => {
    if (!permissionCalculator) return [];
    return permissionCalculator.effectivePermissions;
  };

  const hasRole = (requiredRole) => {
    if (!role) return false;
    
    const roleHierarchy = {
      'super_admin': 3,
      'company_admin': 2,
      'company_user': 1
    };
    
    const userLevel = roleHierarchy[role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  };

  const belongsToCompany = (checkCompanyId) => {
    return companyId === checkCompanyId;
  };

  const canAccessCompany = (checkCompanyId) => {
    return isSuperAdmin() || belongsToCompany(checkCompanyId);
  };

  return {
    // Loading state
    loading,
    error,
    
    // Core permission functions
    hasPermission,
    hasModuleAccess,
    canAccessRoute,
    hasAnyPermission,
    hasAllPermissions,
    
    // Document type permissions
    canAccessDocumentType,
    canCreateDocumentType,
    getAllowedDocumentTypes,
    
    // Navigation permissions
    shouldShowNavItem,
    
    // Utility functions
    getAccessibleModules,
    getModuleActions,
    getPermissionSummary,
    
    // Legacy compatibility
    can,
    canAny,
    canAll,
    canManageUsers,
    canManageCompany,
    canViewAllCompanies,
    canUploadDocuments,
    canViewStats,
    canManageContacts,
    canDeleteDocuments,
    canEditCompanySettings,
    getAllPermissions,
    
    // Role checks (unchanged)
    hasRole,
    isSuperAdmin,
    isCompanyAdmin,
    isCompanyUser,
    
    // Company checks (unchanged)
    belongsToCompany,
    canAccessCompany,
    
    // State
    role,
    companyId,
    userPermissions,
    companyLimits,
    
    // Constants for external use
    MODULES,
    ACTIONS,
  };
}

export default usePermissions;
